import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionPlans, settingsItems } from '@/lib/mock-data';
import type { SubscriptionTier } from '@/lib/types';
import { TestimonialCarousel } from '@/components/paywall/testimonial-carousel';
import { PlanToggle } from '@/components/paywall/plan-toggle';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { logAnalyticsEvent } from '@/lib/firebase/analytics';

export default function PaywallPage() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier>('pro');

  useEffect(() => {
    document.title = 'Subscribe - Deepcheck';
    logAnalyticsEvent('paywall_view');
  }, []);

  const plan = subscriptionPlans.find((p) => p.id === selectedPlan);

  const handleSubscribe = () => {
    logAnalyticsEvent('subscribe_click', { plan: selectedPlan });

    if (plan?.polarProductId) {
      window.location.href = `/api/checkout?products=${plan.polarProductId}`;
      return;
    }

    console.error('Polar Product ID missing for plan:', selectedPlan);
    alert('Configuration Error: Product ID missing. Please contact support.');
  };

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-md mx-auto space-y-6">
        {/* Close Button */}
        <div className="flex justify-start">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Title */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-archivo-black)' }}>Deepcheck Pro</h1>
          <p className="text-xl text-gray-600">Scan Online Presences</p>
        </div>

        {/* Testimonials */}
        <TestimonialCarousel />

        {/* Cancel Anytime */}
        <p className="text-center text-gray-600 text-base">Cancel anytime</p>

        {/* Plan Toggle */}
        <PlanToggle selected={selectedPlan} onSelect={setSelectedPlan} />

        {/* Pricing Display */}
        {plan && (
          <div className="flex justify-between items-start px-2">
            <div className="text-left space-y-1">
              <div className="text-2xl font-bold text-gray-900">
                {plan.id === 'pro' ? 'Pro' : 'Max'}
              </div>
              <div className="text-gray-600 text-sm">
                {plan.periodLabel === 'per Month' ? 'Only' : 'just'} ₺
                {plan.price.toFixed(2).replace('.', ',')} {plan.periodLabel}
              </div>
            </div>
            <div className="text-right space-y-1">
              <div className="text-2xl font-bold text-gray-900">
                ₺{plan.dailyRate.toFixed(2).replace('.', ',')}
              </div>
              <div className="text-gray-500 text-sm">{plan.dailyRateLabel}</div>
            </div>
          </div>
        )}

        {/* Subscribe Button */}
        <Button
          onClick={handleSubscribe}
          className="w-full h-14 bg-black hover:bg-gray-800 text-white font-semibold text-lg"
        >
          Search Now
        </Button>

        {/* Footer Links */}
        <div className="flex justify-center gap-6 text-sm text-gray-600">
          <button
            onClick={() => {
              const userId = settingsItems.find(item => item.type === 'user-id')?.value || 'Unknown';
              const subject = encodeURIComponent('Restore Purchase - Deepcheck');
              const body = encodeURIComponent(`I have this User ID: ${userId}. I want to restore my purchase.`);
              window.location.href = `mailto:shauv@hi2.in?subject=${subject}&body=${body}`;
            }}
            className="hover:text-gray-900"
          >
            Restore
          </button>
        </div>
      </div>
    </main>
  );
}
